<template>
  <Dialog v-model="visible" :title="`结算单详情 ${bill?.no || ''}`" width="960px">
    <div v-loading="loading">
      <el-descriptions :column="3" border class="mb-16px">
        <el-descriptions-item label="状态">
          {{ labelOf(AREA_AGENT_SETTLE_STATUS, bill?.status) }}
        </el-descriptions-item>
        <el-descriptions-item label="参与折后">￥{{ fenToYuan(bill?.goodsAmount || 0) }}</el-descriptions-item>
        <el-descriptions-item label="应发合计">￥{{ fenToYuan(bill?.bonusAmount || 0) }}</el-descriptions-item>
        <el-descriptions-item label="订单数">{{ bill?.orderCount }}</el-descriptions-item>
        <el-descriptions-item label="代理数">{{ bill?.partnerCount }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ bill?.remark || '-' }}</el-descriptions-item>
      </el-descriptions>

      <el-divider content-position="left">代理明细（拿满）</el-divider>
      <el-table :data="bill?.items || []" size="small" max-height="280">
        <el-table-column label="订单" prop="orderId" width="90" />
        <el-table-column label="会员" min-width="110">
          <template #default="{ row }">{{ row.nickname || row.userId }}</template>
        </el-table-column>
        <el-table-column label="类型" width="90">
          <template #default="{ row }">{{ labelOf(AREA_AGENT_TYPE, row.agentType) }}</template>
        </el-table-column>
        <el-table-column label="辖区" prop="areaId" width="80" />
        <el-table-column label="费率%" prop="bonusRate" width="70" />
        <el-table-column label="等级" prop="levelName" width="90" />
        <el-table-column label="系统额" width="100">
          <template #default="{ row }">￥{{ fenToYuan(row.systemAmount || 0) }}</template>
        </el-table-column>
        <el-table-column label="调后额" width="140">
          <template #default="{ row }">
            <template v-if="bill?.status === 0">
              <el-input-number
                v-model="adjustMap[row.id!]"
                :min="0"
                :precision="2"
                size="small"
                class="!w-110px"
              />
            </template>
            <span v-else>￥{{ fenToYuan(row.adjustAmount || 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="bill?.status === 0" label="操作" width="90">
          <template #default="{ row }">
            <el-button
              v-hasPermi="['distribution:area-agent:settle:create']"
              link
              type="primary"
              @click="saveAdjust(row)"
            >
              保存
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-divider content-position="left">订单明细</el-divider>
      <el-table :data="bill?.orders || []" size="small" max-height="240">
        <el-table-column label="订单ID" prop="orderId" width="100" />
        <el-table-column label="leaf" prop="matchLeafAreaId" width="80" />
        <el-table-column label="省/市/区" min-width="140">
          <template #default="{ row }">
            {{ row.provinceId }}/{{ row.cityId }}/{{ row.districtId }}
          </template>
        </el-table-column>
        <el-table-column label="参与折后" width="100">
          <template #default="{ row }">￥{{ fenToYuan(row.goodsDiscounted || 0) }}</template>
        </el-table-column>
        <el-table-column label="锁定" prop="lockStatus" width="70" />
      </el-table>
    </div>
    <template #footer>
      <el-button @click="visible = false">关 闭</el-button>
      <el-button
        v-if="bill?.status === 0"
        v-hasPermi="['distribution:area-agent:settle:confirm']"
        type="primary"
        @click="doConfirm"
      >
        确认
      </el-button>
      <el-button
        v-if="bill?.status === 0"
        v-hasPermi="['distribution:area-agent:settle:cancel']"
        type="danger"
        @click="doCancel"
      >
        作废
      </el-button>
      <el-button
        v-if="bill?.status === 1"
        v-hasPermi="['distribution:area-agent:settle:settle']"
        type="success"
        @click="doSettle"
      >
        结算入账
      </el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { fenToYuan, yuanToFen, fenToYuanNumber } from '@/utils'
import * as SettleApi from '@/api/mall/trade/areaAgent/settle'
import { AREA_AGENT_SETTLE_STATUS, AREA_AGENT_TYPE, labelOf } from '../constants'

defineOptions({ name: 'AreaAgentSettleDetailDialog' })
const emit = defineEmits(['success'])
const message = useMessage()
const visible = ref(false)
const loading = ref(false)
const bill = ref<SettleApi.AreaAgentSettleBillVO>()
const adjustMap = reactive<Record<number, number>>({})

const load = async (id: number) => {
  loading.value = true
  try {
    bill.value = await SettleApi.getAreaAgentSettle(id)
    Object.keys(adjustMap).forEach((k) => delete adjustMap[Number(k)])
    ;(bill.value?.items || []).forEach((it) => {
      if (it.id != null) {
        adjustMap[it.id] = fenToYuanNumber(it.adjustAmount ?? it.systemAmount ?? 0)
      }
    })
  } finally {
    loading.value = false
  }
}

const open = async (id: number) => {
  visible.value = true
  await load(id)
}

const saveAdjust = async (row: NonNullable<SettleApi.AreaAgentSettleBillVO['items']>[0]) => {
  const yuan = adjustMap[row.id!]
  await SettleApi.adjustAreaAgentSettle({
    billId: bill.value!.id!,
    itemId: row.id!,
    adjustAmount: yuanToFen(yuan ?? 0)
  })
  message.success('调额已保存')
  await load(bill.value!.id!)
  emit('success')
}

const doConfirm = async () => {
  await message.confirm('确认后不可再调额，是否继续？')
  await SettleApi.confirmAreaAgentSettle(bill.value!.id!)
  message.success('已确认')
  visible.value = false
  emit('success')
}

const doCancel = async () => {
  await message.confirm('作废将解锁订单，是否继续？')
  await SettleApi.cancelAreaAgentSettle(bill.value!.id!)
  message.success('已作废')
  visible.value = false
  emit('success')
}

const doSettle = async () => {
  await message.confirm('确认执行结算入账？')
  await SettleApi.settleAreaAgentSettle(bill.value!.id!)
  message.success('已入账')
  visible.value = false
  emit('success')
}

defineExpose({ open })
</script>
